import {
  DownloadIcon,
  FolderIcon,
  Loader2Icon,
  XIcon,
} from 'lucide-react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { ConversationDocumentEntry } from '../collect-conversation-documents';
import { getDocumentContent, getDocumentTitle } from '../document-artifact';
import { getChatErrorMessage } from '../chat-errors';
import { downloadRemoteFile } from '../download-remote-file';
import { exportChatDocument } from '../services/chat.api';
import { getDocumentGeneration } from '../services/document-generation.api';
import { ChatSidePanelShell, CHAT_CONVERSATION_DOCUMENTS_PANEL_WIDTH_CLASS } from './ChatSidePanelShell';
import { ChatDocumentKindIcon } from './ChatDocumentKindIcon';
import { useState, type MouseEvent } from 'react';

type ChatConversationDocumentsPanelProps = {
  conversationId?: string;
  documents: ConversationDocumentEntry[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenDocument: (document: ChatDocumentDTO) => void;
};

function sanitizeFilename(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64) || 'document';
}

async function triggerFileDownload(
  downloadUrl: string,
  filename: string,
  options: { withCredentials: boolean } = { withCredentials: true },
) {
  await downloadRemoteFile(downloadUrl, filename, options);
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

async function downloadConversationDocument(
  document: ConversationDocumentEntry,
  conversationId?: string,
) {
  if (document.sourceUrl) {
    await triggerFileDownload(
      document.sourceUrl,
      document.filename ?? `${sanitizeFilename(getDocumentTitle(document))}.bin`,
    );
    return;
  }

  if (document.codegenContext) {
    const status = await getDocumentGeneration(
      document.codegenContext.conversationId,
      document.codegenContext.generationId,
    );

    if (status.status !== 'succeeded' || !status.result) {
      throw new Error('generation_not_ready');
    }

    // URL présignée du stockage : pas de cookie (cohérent avec la carte de génération).
    await triggerFileDownload(status.result.downloadUrl, status.result.filename, { withCredentials: false });
    return;
  }

  if (conversationId && document.kind === 'assistant-brief') {
    const exported = await exportChatDocument(conversationId, document.documentId, 'docx');
    await triggerFileDownload(exported.downloadUrl, exported.filename);
    return;
  }

  const content = getDocumentContent(document).trim();
  if (!content) {
    throw new Error('empty_document');
  }

  triggerBlobDownload(
    new Blob([content], { type: 'text/markdown;charset=utf-8' }),
    `${sanitizeFilename(getDocumentTitle(document))}.md`,
  );
}

function formatDocumentTimestamp(value?: string): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      month: 'short',
    }).format(date);
  } catch {
    return null;
  }
}

function ConversationDocumentCard({
  conversationId,
  document,
  onOpenDocument,
}: {
  conversationId?: string;
  document: ConversationDocumentEntry;
  onOpenDocument: (document: ChatDocumentDTO) => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const title = getDocumentTitle(document);
  const subtitle = document.filename ?? document.mediaType ?? document.summary ?? null;
  const timestamp = formatDocumentTimestamp(document.createdAt);
  const isDownloadDisabled = document.status === 'streaming' || isDownloading;

  async function handleDownload(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();

    if (isDownloadDisabled) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadConversationDocument(document, conversationId);
      toast.success('Téléchargement lancé.');
    } catch (error) {
      if (error instanceof Error && error.message === 'generation_not_ready') {
        toast.info('Le document est encore en cours de génération.');
        return;
      }

      toast.error(getChatErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Card
      className={cn(
        'cursor-pointer border-border/70 bg-card/90 transition hover:border-border hover:bg-card',
        document.status === 'streaming' && 'opacity-80',
      )}
      onClick={() => onOpenDocument(document)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenDocument(document);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <CardContent className="flex min-w-0 items-center gap-3 px-3 py-3">
        <div className="shrink-0">
          <ChatDocumentKindIcon document={document} size="sm" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{title}</p>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
            {subtitle ? <span className="truncate">{subtitle}</span> : null}
            {subtitle && timestamp ? <span aria-hidden className="shrink-0 opacity-60">·</span> : null}
            {timestamp ? <span className="shrink-0">{timestamp}</span> : null}
          </div>
        </div>

        <Button
          aria-label={`Télécharger ${title}`}
          className="size-8 shrink-0 rounded-full"
          disabled={isDownloadDisabled}
          onClick={(event) => void handleDownload(event)}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          {isDownloading
            ? <Loader2Icon className="size-4 animate-spin" />
            : <DownloadIcon className="size-4" />}
        </Button>
      </CardContent>
    </Card>
  );
}

function PanelContent({
  conversationId,
  documents,
  onClose,
  onOpenDocument,
}: {
  conversationId?: string;
  documents: ConversationDocumentEntry[];
  onClose: () => void;
  onOpenDocument: (document: ChatDocumentDTO) => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-sidebar">
      <div className="flex items-center justify-between gap-3 border-b border-border/50 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <FolderIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-foreground">Documents de la conversation</h2>
            <p className="text-xs text-muted-foreground">
              {documents.length > 1
                ? `${documents.length} documents`
                : documents.length === 1
                  ? '1 document'
                  : 'Aucun document'}
            </p>
          </div>
        </div>

        <Button
          aria-label="Fermer la liste des documents"
          className="rounded-full"
          onClick={onClose}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <XIcon className="size-4" />
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="flex flex-col gap-2 p-3">
          {documents.length === 0 ? (
            <div className="rounded-[var(--radius-surface-2xl)] border border-dashed border-border/70 bg-muted/15 px-4 py-8 text-center text-sm text-muted-foreground">
              Aucun document n’a encore été ajouté ou généré dans cette conversation.
            </div>
          ) : (
            documents.map((document) => (
              <ConversationDocumentCard
                conversationId={conversationId}
                document={document}
                key={document.documentId}
                onOpenDocument={onOpenDocument}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export function ChatConversationDocumentsPanel({
  conversationId,
  documents,
  isOpen,
  onOpenChange,
  onOpenDocument,
}: ChatConversationDocumentsPanelProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet onOpenChange={onOpenChange} open={isOpen}>
        <SheetContent
          className="w-full max-w-sm gap-0 bg-sidebar p-0 sm:max-w-sm [&>button]:hidden"
          data-testid="conversation-documents-panel"
          showCloseButton={false}
          side="right"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Documents de la conversation</SheetTitle>
            <SheetDescription>Liste des documents attachés ou générés dans cette conversation.</SheetDescription>
          </SheetHeader>
          <PanelContent
            conversationId={conversationId}
            documents={documents}
            onClose={() => onOpenChange(false)}
            onOpenDocument={onOpenDocument}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <ChatSidePanelShell
      isOpen={isOpen}
      openWidthClass={CHAT_CONVERSATION_DOCUMENTS_PANEL_WIDTH_CLASS}
      testId="conversation-documents-panel"
    >
      <PanelContent
        conversationId={conversationId}
        documents={documents}
        onClose={() => onOpenChange(false)}
        onOpenDocument={onOpenDocument}
      />
    </ChatSidePanelShell>
  );
}
